import { supabase } from '@/integrations/supabase/client';

/**
 * Checks if onboarding is needed for a family
 * @param familyId - The family ID to check
 * @param familyName - The family name to check
 * @returns Promise<boolean> - true if onboarding is needed
 */
export const isOnboardingNeeded = async (familyId: string, familyName: string): Promise<boolean> => {
  // Check if family name suggests it needs configuration
  const defaultNames = ['Nouvelle famille', 'Nouveau clan', 'Ma Famille', 'Mon Workspace'];
  const hasDefaultName = defaultNames.some(name => 
    familyName.toLowerCase().includes(name.toLowerCase())
  );
  
  if (hasDefaultName) {
    return true;
  }
  
  // Check if there are any person_profiles for this family (excluding system-created ones)
  try {
    const { data: personProfiles, error } = await supabase
      .from('person_profiles')
      .select('id')
      .eq('family_id', familyId)
      .limit(1);
      
    if (error) {
      console.error('Error checking person profiles:', error);
      return true; // If we can't check, assume onboarding is needed
    }
    
    // If no person profiles exist, onboarding is needed
    return !personProfiles || personProfiles.length === 0;
    
  } catch (error) {
    console.error('Error in onboarding detection:', error);
    return true; // If error, assume onboarding is needed
  }
};

/**
 * Checks if redirect to onboarding is needed and sets appropriate flags
 * @param familyId - The family ID to check
 * @param familyName - The family name
 * @returns Promise<boolean> - true if onboarding is needed
 */
export const shouldRedirectToOnboarding = async (familyId: string, familyName: string): Promise<boolean> => {
  // GUARDRAIL: If we're in new family creation mode, don't trigger existing family onboarding
  const currentMode = localStorage.getItem('onboardingMode');
  if (currentMode === '1') {
    console.log('🛑 shouldRedirectToOnboarding: Blocked due to new family creation mode');
    return false;
  }

  const needsOnboarding = await isOnboardingNeeded(familyId, familyName);
  
  if (needsOnboarding) {
    // Store the family ID so onboarding can update it instead of creating new
    localStorage.setItem('onboardingFamilyId', familyId);
    localStorage.setItem('onboardingMode', 'existing');
    console.log('✅ shouldRedirectToOnboarding: Setting existing family onboarding mode');
    return true;
  }
  
  return false;
};

/**
 * Starts new family onboarding by clearing existing onboarding data and setting the flag
 */
export const startNewFamilyOnboarding = () => {
  localStorage.removeItem('onboardingFamilyId');
  localStorage.removeItem('currentFamilyId'); // Clear current family to ensure clean slate
  localStorage.setItem('onboardingMode', '1');
  console.log('🚀 Starting new family onboarding with clean slate');
};

/**
 * Legacy function for backward compatibility - now uses flags instead of forced redirects
 * @param familyId - The family ID to check
 * @param familyName - The family name
 */
export const redirectBasedOnOnboardingStatus = async (familyId: string, familyName: string) => {
  // GUARDRAIL: If we're in new family creation mode, don't trigger existing family onboarding
  const currentMode = localStorage.getItem('onboardingMode');
  if (currentMode === '1') {
    console.log('🛑 redirectBasedOnOnboardingStatus: Blocked due to new family creation mode');
    return;
  }

  const needsOnboarding = await shouldRedirectToOnboarding(familyId, familyName);
  
  if (needsOnboarding) {
    // Instead of forcing redirect, just set the flag - Dashboard will handle the navigation
    console.log('Onboarding needed for family:', familyName);
  }
};